import React from 'react';
import Skeleton from './Skeleton';

/**
 * TrainingSkeleton
 * Shimmer placeholder for the Training page:
 *   - header (title + action buttons)
 *   - 4 summary stat tiles
 *   - courses catalogue grid
 *   - carer progress board rows
 * Built from the base <Skeleton /> block (pure Tailwind shimmer).
 */

const CARD =
  'bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)]';

const S = Skeleton;

const TileSkeleton = () => (
  <div className={`${CARD} p-5`}>
    <div className="flex items-start justify-between">
      <S className="h-3 w-20 rounded-md" />
      <S className="w-11 h-11 rounded-2xl" />
    </div>
    <S className="h-8 w-14 rounded-lg mt-3" />
  </div>
);

const CourseSkeleton = () => (
  <div className="border border-[#EEF2F8] dark:border-gray-800 rounded-2xl p-4">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1 space-y-2">
        <S className="h-4 w-32 rounded-md" />
        <S className="h-4 w-24 rounded-md" />
      </div>
      <S className="w-9 h-9 rounded-lg shrink-0" />
    </div>
    <S className="h-3 w-full rounded-md mt-3" />
    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#EEF2F8] dark:border-gray-800">
      <S className="h-3 w-20 rounded-md" />
      <S className="h-3 w-14 rounded-md" />
    </div>
  </div>
);

const BoardRowSkeleton = () => (
  <div className="flex flex-col lg:flex-row lg:items-start gap-4 px-6 py-5">
    <div className="w-full lg:w-[230px] shrink-0 flex items-center gap-3">
      <S className="w-10 h-10 rounded-full shrink-0" />
      <div className="min-w-0 space-y-2 flex-1">
        <S className="h-4 w-28 rounded-md" />
        <S className="h-3 w-20 rounded-md" />
        <S className="h-1.5 w-24 rounded-full" />
      </div>
    </div>
    <div className="flex-1 flex flex-wrap gap-3">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="w-full sm:w-[300px] border border-[#EEF2F8] dark:border-gray-800 rounded-2xl p-3.5 space-y-3">
          <div className="flex items-center gap-2">
            <S className="h-4 flex-1 rounded-md" />
            <S className="h-4 w-16 rounded-md" />
          </div>
          <S className="h-8 w-full rounded-lg" />
          <S className="h-8 w-full rounded-lg" />
        </div>
      ))}
    </div>
  </div>
);

const TrainingSkeleton = () => (
  <div className="w-full space-y-5 pb-12">
    {/* Header */}
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div>
        <S className="h-7 w-40 rounded-xl" />
        <S className="h-3.5 w-80 max-w-full rounded-md mt-3" />
      </div>
      <div className="flex gap-2">
        <S className="h-[42px] w-36 rounded-xl" />
        <S className="h-[42px] w-36 rounded-xl" />
      </div>
    </div>

    {/* Summary tiles */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => <TileSkeleton key={i} />)}
    </div>

    {/* Courses catalogue */}
    <div className={`${CARD} p-5`}>
      <S className="h-4 w-28 rounded-md mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {Array.from({ length: 3 }).map((_, i) => <CourseSkeleton key={i} />)}
      </div>
    </div>

    {/* Carer board */}
    <div className={CARD}>
      <div className="px-6 py-3 border-b border-[#EEF2F8] dark:border-gray-800">
        <S className="h-3 w-36 rounded-md" />
      </div>
      <div className="divide-y divide-[#EEF2F8]">
        {Array.from({ length: 3 }).map((_, i) => <BoardRowSkeleton key={i} />)}
      </div>
    </div>
  </div>
);

export default TrainingSkeleton;
